<template>
  <div>
    <b-card no-body class="customer-info-card">
      <div class="ci-header">
        <span class="ci-title">Customer Information</span>
        <b-badge pill :variant="variantStatus(userData.user)">
          {{ formatStatus(userData.user) }}
        </b-badge>
      </div>

      <div class="info-row info-row-main">
        <div class="info-cell">
          <div class="ck"><feather-icon icon="UserIcon" size="12" /> Name</div>
          <div class="cv">
            {{ userData.user.firstName }} {{ userData.user.lastName }}
            <span class="text-muted" style="font-weight: 400; font-size: 10px">
              ({{ userData.user.gender === "M" ? "Male" : "Female" }})
            </span>
          </div>
          <div class="text-muted" style="font-size: 11px; margin-top: 2px">
            {{ userData.user.userId }}
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="CheckCircleIcon" size="12" /> Account Status
          </div>
          <div class="cv">
            <b-badge
              class="text-wrap"
              pill
              :variant="variantStatus(userData.user)"
            >
              {{ formatStatus(userData.user) }}
            </b-badge>
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="UserPlusIcon" size="12" /> BDM Manager
          </div>
          <div class="cv">
            <div class="d-flex align-items-center" style="gap: 4px">
              <v-select
                v-model="selectedBdmManager"
                :options="bdmManagerOptions"
                label="text"
                :filterable="false"
                placeholder="Search BDM..."
                @search="onSearchBdmManager"
                @open="onOpenBdmManager"
                @scroll-bottom="onScrollBottomBdmManager"
                no-options-text="Not found"
                :clearable="false"
                class="ref-bdm-select flex-grow-1"
              >
                <template #list-footer>
                  <div
                    v-if="bdmManagerHasMore"
                    ref="loadMoreTriggerBdmManager"
                    class="text-center pb-1 text-muted"
                  >
                    <b-spinner small />
                  </div>
                </template>
              </v-select>
              <b-button
                variant="primary"
                size="sm"
                class="btn-apply"
                @click="handleApplyBdmManager"
                >Apply</b-button
              >
            </div>
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="CheckSquareIcon" size="12" /> Is Account
            Managed?
          </div>
          <div class="cv">
            <div class="d-flex align-items-center" style="gap: 6px">
              <b-form-checkbox v-model="isAccountManaged" switch size="sm" />
              <span
                :style="{
                  fontWeight: 700,
                  color: isAccountManaged ? '#198754' : '#dc3545',
                  fontSize: '12px',
                }"
              >
                {{ isAccountManaged ? "Yes" : "No" }}
              </span>
            </div>
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="LinkIcon" size="12" /> Affiliate
          </div>
          <div class="cv">
            {{
              userData.user.primaryAffiliateName
                ? userData.user.primaryAffiliateName
                : "—"
            }}
          </div>
        </div>
      </div>

      <div class="info-row info-row-main">
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="EditIcon" size="12" /> Profile Type
          </div>
          <div class="cv">
            <div class="d-flex align-items-center" style="gap: 4px">
              <v-select
                v-model="selectedProfile"
                :options="profileTypes"
                label="text"
                :reduce="(opt) => opt.value"
                :clearable="false"
                placeholder="Select profile..."
                no-options-text="Not found"
                class="ref-bdm-select flex-grow-1"
              />
              <b-button
                variant="primary"
                size="sm"
                class="btn-apply"
                @click="handleApplyProfile"
                >Apply</b-button
              >
            </div>
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="FileTextIcon" size="12" /> Bet Templates
            <b-button
              v-if="userData.user.bettingTemplate"
              variant="danger"
              size="sm"
              class="btn-apply"
              @click="handleClearBetTemplate"
              >Clear</b-button
            >
          </div>
          <div class="cv">
            <div class="d-flex align-items-center" style="gap: 4px">
              <b-form-select
                v-model="selectedBetTemplate"
                :options="betTemplateOptions"
                size="sm"
                class="cell-select"
              />
              <b-button
                variant="primary"
                size="sm"
                class="btn-apply"
                @click="handleApplyBetTemplate"
                >Apply</b-button
              >
            </div>
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="UserIcon" size="12" /> Username
          </div>
          <div class="cv">{{ userData.user.username }}</div>
          <div style="font-size: 11px">
            <a
              class="font-weight-bold"
              :href="`mailto:${userData.user.email}`"
              >{{ userData.user.email }}</a
            >
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="PhoneIcon" size="12" /> Contact
          </div>
          <div class="cv">
            <div>{{ userData.user.phoneNumber }}</div>
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="MapPinIcon" size="12" /> Address / State
          </div>
          <div class="cv">
            {{ userData.user.fullAddress || userData.user.state }}
          </div>
          <div class="ck" style="margin-top: 4px">
            <feather-icon icon="HashIcon" size="12" /> Postcode
          </div>
          <div class="cv">{{ userData.user.postCode || "—" }}</div>
        </div>
      </div>

      <div class="info-row info-row-main">
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="CalendarIcon" size="12" /> Date of Birth
          </div>
          <div class="cv">{{ formatDob(userData.user.dob) }}</div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="DollarSignIcon" size="12" /> Deposit Limit
          </div>
          <div class="cv">
            <b-spinner v-if="loadingDepositLimits" small />
            <div
              v-else
              class="d-flex flex-column align-items-start"
              style="gap: 8px"
            >
              <div
                class="d-flex align-items-center"
                style="gap: 4px; flex-shrink: 0"
              >
                <div
                  class="deposit-limit"
                  :class="
                    depositLimits.length > 0
                      ? 'text-primary'
                      : 'deposit-limit-disable'
                  "
                  @click="
                    depositLimits.length > 0 && handleOpenDepositLimitModal()
                  "
                >
                  {{ depositLimits.length > 0 ? "YES" : "NO" }}
                </div>
                <feather-icon
                  :icon="
                    depositLimits.length > 0 ? 'CheckCircleIcon' : 'XCircleIcon'
                  "
                  :class="
                    depositLimits.length > 0 ? 'text-success' : 'text-danger'
                  "
                  size="12"
                />
              </div>
              <div v-if="depositLimits.length > 0">
                <div v-for="limit in depositLimits" :key="limit.period">
                  <p class="text-muted">
                    Period: {{ formatPeriod(limit.period) }}
                  </p>
                  <p class="text-muted">
                    Limit: {{ formatCurrencyBuiltInFunc(limit.amount) }}
                  </p>
                  <p v-if="limit.quota != null" class="text-muted">
                    Remaining: {{ formatCurrencyBuiltInFunc(limit.quota) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="MailIcon" size="12" /> Marketing Preferences
          </div>
          <div class="cv">
            <div class="d-flex align-items-center flex-wrap" style="gap: 4px">
              <template v-if="userTags.length > 0">
                <b-badge
                  variant="primary"
                  v-for="tag in userTags"
                  :key="tag.tagId"
                  style="font-size: 10px"
                  >{{ tag.tagName }}</b-badge
                >
              </template>
              <span v-else class="text-muted" style="font-size: 11px"
                >No groups Selected</span
              >
              <a
                href="#"
                style="font-size: 10px; margin-left: 4px"
                @click.prevent="showModalMarketing"
                >edit</a
              >
            </div>
          </div>
        </div>
        <div class="info-cell cell-ref-by-spanning">
          <div class="ck">
            <feather-icon icon="UserCheckIcon" size="12" /> Ref By
          </div>
          <div class="cv">
            <div class="d-flex align-items-center flex-nowrap" style="gap: 8px">
              <b-form-radio-group
                v-model="selectedTypeAffiliate"
                :options="typeAffiliatesOptions"
                name="type-aff"
                class="mb-0 custom-radio-group"
                @change="handleChangeTypeAff"
              />
              <div
                class="d-flex align-items-center flex-grow-1"
                style="gap: 4px"
              >
                <template v-if="selectedTypeAffiliate === 'customers'">
                  <v-select
                    v-model="selectedBDM"
                    :options="bdmOptions"
                    label="text"
                    :filterable="false"
                    :placeholder="`Search...`"
                    @search="onSearchBDM"
                    @open="onOpenBDM"
                    @scroll-bottom="onScrollBottomBDM"
                    no-options-text="Not found"
                    :clearable="false"
                    class="ref-bdm-select flex-grow-1"
                  >
                    <template #list-footer>
                      <div
                        v-if="affiliateHasMore"
                        ref="loadMoreTrigger"
                        class="text-center pb-1 text-muted"
                      >
                        <b-spinner small />
                      </div>
                    </template>
                  </v-select>
                </template>
                <template v-else>
                  <b-form-input
                    v-model="freeTextInput"
                    placeholder="Enter free text..."
                    size="sm"
                    class="flex-grow-1"
                    style="height: 30px; font-size: 11px"
                  />
                </template>
                <b-button
                  size="sm"
                  variant="primary"
                  class="btn-assign"
                  @click="handleAssignBDM"
                  >Assign</b-button
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="info-row info-row-main">
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="ClockIcon" size="12" /> Registration Date
          </div>
          <div class="cv">
            {{
              userData.user.createdTime
                ? format(new Date(userData.user.createdTime), "MMM d, yyyy")
                : "—"
            }}
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="LogInIcon" size="12" /> Last Logged In
          </div>
          <div class="cv">
            {{ formatDateAndTime(userData.user.lastLoginDate) }}
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="StarIcon" size="12" /> Last Bet Date
          </div>
          <div class="cv">
            {{
              userData.latestBetCreatedTime
                ? formatDateAndTime(userData.latestBetCreatedTime)
                : "—"
            }}
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="DownloadIcon" size="12" /> Last Withdrawal
          </div>
          <div class="wd-row-item">
            <span class="text-muted">Date</span
            ><span class="font-weight-bold">{{
              userData.latestWithdrawal
                ? format(
                    new Date(userData.latestWithdrawal.createdTime),
                    "dd/MM/yyyy"
                  )
                : "—"
            }}</span>
          </div>
          <div class="wd-row-item">
            <span class="text-muted">Amount</span
            ><span class="font-weight-bold">{{
              userData.latestWithdrawal
                ? formatCurrencyBuiltInFunc(
                    userData.latestWithdrawal.amount || 0
                  )
                : "—"
            }}</span>
          </div>
        </div>
        <div class="info-cell">
          <div class="ck">
            <feather-icon icon="UploadIcon" size="12" /> Deposit Since Last
            Withdrawal
          </div>
          <template v-if="userData.depositStatistics">
            <div class="wd-row-item">
              <span class="text-muted">Count</span
              ><span class="font-weight-bold">{{
                userData.depositStatistics.count
              }}</span>
            </div>
            <div class="wd-row-item">
              <span class="text-muted">Total</span
              ><span class="font-weight-bold">{{
                formatCurrencyBuiltInFunc(userData.depositStatistics.total || 0)
              }}</span>
            </div>
          </template>
          <div v-else class="text-muted" style="font-size: 11px">—</div>
        </div>
      </div>

      <div class="ci-revenue">
        <div class="ci-revenue-header">
          <span class="font-weight-bold" style="font-size: 12px">Revenue</span>
        </div>
        <div style="overflow-x: auto">
          <RevenueTable />
        </div>
      </div>
    </b-card>

    <MarketingPreferencesModal />
    <DepositLimitModal />
    <BankAccountModal :bankAccounts="userData.bankAccounts" />
    <OskoProfileModal />
    <FirstWithdrawalModal />
    <BoostModal />
  </div>
</template>

<script>
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import Spinner from "@core/components/Spinner";
import {
  BCard,
  BButton,
  BFormCheckbox,
  BFormSelect,
  BBadge,
  BFormRadioGroup,
  BFormRadio,
  BSpinner,
  BFormInput,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { format, differenceInYears, differenceInHours } from "date-fns";
import { mapActions, mapState } from "vuex";
import MarketingPreferencesModal from "./modals/MarketingPreferencesModal.vue";
import DepositLimitModal from "./modals/DepositLimitModal.vue";
import BankAccountModal from "./modals/BankAccountModal.vue";
import OskoProfileModal from "./modals/OskoProfileModal.vue";
import FirstWithdrawalModal from "./modals/FirstWithdrawalModal.vue";
import BoostModal from "./modals/BoostModal.vue";
import RevenueTable from "./components/RevenueTable.vue";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BCard,
    BButton,
    Spinner,
    BFormCheckbox,
    BFormSelect,
    BBadge,
    vSelect,
    BFormRadioGroup,
    BFormRadio,
    BSpinner,
    BFormInput,
    MarketingPreferencesModal,
    DepositLimitModal,
    BankAccountModal,
    OskoProfileModal,
    FirstWithdrawalModal,
    BoostModal,
    RevenueTable,
  },
  data() {
    return {
      bdmOptions: [],
      selectedBDM: null,
      affiliatePage: 1,
      affiliateSearch: "",
      affiliateHasMore: true,
      loadingAffiliate: false,
      observer: null,
      selectedTypeAffiliate: "customers",
      typeAffiliatesOptions: [
        { value: "customers", text: "Customer" },
        { value: "freetext", text: "Custom Entry" },
      ],
      freeTextInput: "",
      isAccountManaged: false,
      selectedProfile: null,
      profileTypes: [],
      selectedBdmManager: null,
      bdmManagerOptions: [],
      bdmManagerPage: 1,
      bdmManagerSearch: "",
      bdmManagerHasMore: true,
      loadingBdmManager: false,
      observerBdmManager: null,
      selectedBetTemplate: null,
      betTemplateOptions: [],
      depositLimits: [],
      loadingDepositLimits: false,
    };
  },
  watch: {
    "userData.user.profile": {
      immediate: true,
      handler() {
        this.selectedProfile = this.userData.user.profile || null;
      },
    },
    "userData.user.bettingTemplate": {
      immediate: true,
      handler(val) {
        this.selectedBetTemplate = val || null;
      },
    },
    "userData.user.primaryAffiliate": {
      immediate: true,
      handler() {
        const { primaryAffiliate, primaryAffiliateName } =
          this.userData.user || {};
        this.selectedBdmManager = primaryAffiliate
          ? {
              value: primaryAffiliate,
              text: primaryAffiliateName || primaryAffiliate,
            }
          : null;
      },
    },
    "userData.referrer": {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (!newVal) {
          this.selectedTypeAffiliate = "customers";
          this.selectedBDM = null;
          this.freeTextInput = "";
          return;
        }
        if (newVal.referredByType === "FreeText") {
          this.selectedTypeAffiliate = "freetext";
          this.freeTextInput = newVal.referredById || "";
          this.selectedBDM = null;
        } else {
          this.selectedTypeAffiliate = "customers";
          this.selectedBDM = {
            value: newVal.referredById,
            text: newVal.referredByName,
          };
          this.freeTextInput = "";
        }
      },
    },
    "$route.params.id": {
      handler: async function (newId) {
        const [, profileData] = await Promise.all([
          this.getUserTag(this.userData.user.userId),
          this.getProfileTypes(),
        ]);
        if (profileData)
          this.profileTypes = profileData.map((t) => ({
            value: t.profileTypeId,
            text: t.profileTypeId,
          }));
        this.loadDepositLimits();
        this.loadBettingTemplates();
      },
    },
  },
  computed: {
    ...mapState("dashboard/detail", ["userTags", "loadingSyncTag"]),
    ...mapState("user/detail", ["userData"]),
    ...mapState("user/list", { paginationUser: "pagination" }),
  },
  async mounted() {
    const [, profileData] = await Promise.all([
      this.getUserTag(this.userData.user.userId),
      this.getProfileTypes(),
    ]);
    if (profileData)
      this.profileTypes = profileData.map((t) => ({
        value: t.profileTypeId,
        text: t.profileTypeId,
      }));
    this.loadDepositLimits();
    this.loadBettingTemplates();
  },
  methods: {
    ...mapActions("dashboard/detail", {
      getUserTag: "getUserTag",
    }),
    ...mapActions("user/detail", {
      getProfileTypes: "getProfileTypes",
      getDepositLimitByUser: "getDepositLimitByUser",
      getBettingTemplates: "getBettingTemplates",
      updateBettingTemplate: "updateBettingTemplate",
    }),
    ...mapActions("bdm/affiliates/list", { getAffiliates: "getAffiliates" }),
    ...mapActions("user/list", {
      updateUserProfile: "updateUserProfile",
      getUserList: "getUserList",
    }),
    ...mapActions({ fetchUser: "user/detail/asyncUser" }),
    format,
    showModalMarketing() {
      this.$bvModal.show("marketing-prefs-modal");
    },
    formatDob(dob) {
      return `${format(new Date(dob), "dd/MM/yyyy")} (${differenceInYears(
        new Date(),
        new Date(dob)
      )})`;
    },
    formatDateAndTime(date) {
      return format(new Date(date), "dd/MM/yyyy HH:mm:ss");
    },
    formatStatus(item) {
      const {
        isActive,
        isVerified,
        isBlocked,
        isBanned,
        selfExcluded,
        selfClosed,
        breakStartDate,
        breakTime,
      } = item;
      if (!isActive) return "Inactive";
      if (!isVerified) return "UnVerified";
      if (isBlocked) return "Blocked";
      if (selfClosed) return "Closed";
      if (selfExcluded) return "Self Excluded";
      if (isBanned) return "Banned";
      if (breakStartDate && breakTime && new Date(breakTime) > new Date())
        return `Break (${Math.ceil(
          differenceInHours(new Date(breakTime), new Date(breakStartDate)) / 24
        )}D)`;
      return "Active";
    },
    variantStatus(item) {
      const {
        isActive,
        isVerified,
        isBlocked,
        isBanned,
        selfExcluded,
        selfClosed,
        breakStartDate,
        breakTime,
      } = item;
      if (!isActive || isBanned) return "danger";
      if (!isVerified) return "primary";
      if (isBlocked) return "warning";
      if (selfClosed) return "secondary";
      if (selfExcluded) return "dark";
      if (breakStartDate && breakTime && new Date(breakTime) > new Date())
        return "info";
      return "success";
    },
    handleOpenDepositLimitModal() {
      this.$bvModal.show("deposit-limit-modal");
    },
    formatPeriod(p) {
      const periodMap = { 1: "Daily", 7: "Weekly", 30: "Monthly" };
      return periodMap[p] || periodMap["1"];
    },
    async fetchAffiliates(reset = false) {
      if (this.loadingAffiliate || (!this.affiliateHasMore && !reset)) return;
      this.loadingAffiliate = true;
      try {
        if (reset) {
          this.affiliatePage = 1;
          this.affiliateHasMore = true;
          this.bdmOptions = [];
        }
        const payload = {
          page: this.affiliatePage,
          limit: 10,
          ...(this.affiliateSearch && { search: this.affiliateSearch }),
        };
        const res = await this.getUserList(payload);
        const total = this.paginationUser.total;
        const mapped = res.map((item) => ({
          value: item.userId,
          text: this.formatName(item) || "-",
        }));
        if (reset) this.bdmOptions = mapped;
        else this.bdmOptions.push(...mapped);
        this.affiliateHasMore = this.bdmOptions.length < total;
        if (this.affiliateHasMore) this.affiliatePage++;
      } finally {
        this.loadingAffiliate = false;
        this.$nextTick(() => {
          if (this.affiliateHasMore) this.initObserver();
        });
      }
    },
    initObserver() {
      this.$nextTick(() => {
        const dropdown = document.querySelector(".vs__dropdown-menu");
        if (!dropdown || !this.$refs.loadMoreTrigger) return;
        this.disconnectObserver();
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) this.fetchAffiliates();
          },
          { root: dropdown, rootMargin: "50px" }
        );
        this.observer.observe(this.$refs.loadMoreTrigger);
      });
    },
    disconnectObserver() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
    },
    handleChangeTypeAff() {
      this.bdmOptions = [];
      this.selectedBDM = null;
      this.affiliatePage = 1;
      this.affiliateSearch = "";
      this.freeTextInput = "";
    },
    onOpenBDM() {
      if (this.bdmOptions.length === 0) this.fetchAffiliates(true);
      this.$nextTick(this.initObserver);
    },
    onScrollBottomBDM() {
      this.fetchAffiliates();
    },
    onSearchBDM(search) {
      this.affiliateSearch = search;
      this.affiliatePage = 1;
      this.affiliateHasMore = true;
      this.disconnectObserver();
      this.fetchAffiliates(true);
    },
    formatName(item) {
      return [item.name, item.firstName, item.middleName, item.lastName]
        .filter(Boolean)
        .join(" ");
    },
    async fetchBdmManagerList(reset = false) {
      if (this.loadingBdmManager || (!this.bdmManagerHasMore && !reset)) return;
      this.loadingBdmManager = true;
      try {
        if (reset) {
          this.bdmManagerPage = 1;
          this.bdmManagerHasMore = true;
          this.bdmManagerOptions = [];
        }
        const payload = {
          page: this.bdmManagerPage,
          limit: 10,
          ...(this.bdmManagerSearch && { search: this.bdmManagerSearch }),
        };
        const res = await this.getAffiliates(payload);
        const mapped = (res?.data || []).map((item) => ({
          value: item.code,
          text: this.formatName(item) || "-",
        }));
        if (reset) this.bdmManagerOptions = mapped;
        else this.bdmManagerOptions.push(...mapped);
        this.bdmManagerHasMore =
          this.bdmManagerOptions.length < (res?.pagination?.total || 0);
        if (this.bdmManagerHasMore) this.bdmManagerPage++;
      } finally {
        this.loadingBdmManager = false;
        this.$nextTick(() => {
          if (this.bdmManagerHasMore) this.initObserverBdmManager();
        });
      }
    },
    initObserverBdmManager() {
      this.$nextTick(() => {
        const dropdown = document.querySelector(".vs__dropdown-menu");
        if (!dropdown || !this.$refs.loadMoreTriggerBdmManager) return;
        this.disconnectObserverBdmManager();
        this.observerBdmManager = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) this.fetchBdmManagerList();
          },
          { root: dropdown, rootMargin: "50px" }
        );
        this.observerBdmManager.observe(this.$refs.loadMoreTriggerBdmManager);
      });
    },
    disconnectObserverBdmManager() {
      if (this.observerBdmManager) this.observerBdmManager.disconnect();
      this.observerBdmManager = null;
    },
    onOpenBdmManager() {
      if (this.bdmManagerOptions.length === 0) this.fetchBdmManagerList(true);
      this.$nextTick(this.initObserverBdmManager);
    },
    onScrollBottomBdmManager() {
      this.fetchBdmManagerList();
    },
    onSearchBdmManager(search) {
      this.bdmManagerSearch = search;
      this.bdmManagerPage = 1;
      this.bdmManagerHasMore = true;
      this.disconnectObserverBdmManager();
      this.fetchBdmManagerList(true);
    },
    async loadBettingTemplates() {
      const tagProfile = this.userData.user.profile;
      const data = await this.getBettingTemplates(tagProfile);
      if (data) this.betTemplateOptions = data;
    },
    async handleApplyBetTemplate() {
      if (!this.selectedBetTemplate) return;
      const ok = await this.updateBettingTemplate({
        userId: this.userData.user.userId,
        bettingTemplate: this.selectedBetTemplate,
      });
      if (ok !== undefined) {
        this.showToastSuccess("Bet template updated successfully.");
        this.fetchUser({ id: this.userData.user.userId });
      }
    },
    async handleClearBetTemplate() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$createElement(
          "span",
          { style: "color: black" },
          "Are you sure you want to clear the betting template?"
        ),
        {
          title: "Clear Bet Template",
          okVariant: "danger",
          okTitle: "Clear",
          centered: true,
        }
      );
      if (!confirmed) return;
      const ok = await this.updateBettingTemplate({
        userId: this.userData.user.userId,
        bettingTemplate: null,
      });
      if (ok !== undefined) {
        this.showToastSuccess("Bet template cleared.");
        this.fetchUser({ id: this.userData.user.userId });
      }
    },
    async loadDepositLimits() {
      this.loadingDepositLimits = true;
      try {
        const data = await this.getDepositLimitByUser(
          this.userData.user.userId
        );
        if (data) this.depositLimits = data.sort((a, b) => a.period - b.period);
      } finally {
        this.loadingDepositLimits = false;
      }
    },
    async handleApplyProfile() {
      if (!this.selectedProfile) return;
      try {
        const payload = {
          userId: this.userData.user.userId,
          profile: this.selectedProfile,
        };
        const { data } = await this.updateUserProfile(payload);
        if (data.request) {
          this.showToastSuccess("Profile updated successfully.");
          this.fetchUser({ id: this.userData.user.userId });
        }
      } catch (error) {}
    },
    async handleApplyBdmManager() {
      if (!this.selectedBdmManager?.value) return;
      const { value, text } = this.selectedBdmManager;
      try {
        const payload = {
          userId: this.userData.user.userId,
          primaryAffiliate: value,
          primaryAffiliateName: text,
        };
        const { data } = await this.updateUserProfile(payload);
        if (data.request) {
          this.showToastSuccess("BDM Manager assigned successfully.");
          this.fetchUser({ id: this.userData.user.userId });
        }
      } catch (error) {}
    },
    async handleAssignBDM() {
      if (this.selectedTypeAffiliate === "freetext") {
        if (!this.freeTextInput?.trim()) return;
        const payload = {
          userId: this.userData.user.userId,
          referredByType: "FreeText",
          referredById: this.freeTextInput.trim(),
        };
        const { data } = await this.updateUserProfile(payload);
        if (data.request) {
          this.showToastSuccess("Assigned successfully.");
          this.fetchUser({ id: this.userData.user.userId });
        }
      } else {
        if (!this.selectedBDM?.value) return;
        const payload = {
          userId: this.userData.user.userId,
          referredByType: "User",
          referredById: this.selectedBDM.value,
        };
        const { data } = await this.updateUserProfile(payload);
        if (data.request) {
          this.showToastSuccess("Assigned successfully.");
          this.fetchUser({ id: this.userData.user.userId });
        }
      }
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return { formatCurrencyBuiltInFunc, showToastError, showToastSuccess };
  },
};
</script>

<style lang="scss" scoped>
.customer-info-card {
  border: 1px solid #e2e6ea;
  border-radius: 6px;
  max-width: 100%;
  overflow-x: hidden;
}

.ci-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #e2e6ea;
}

.ci-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a1f36;
}

.info-row {
  display: grid;
  border-bottom: 1px solid #eef0f4;
  grid-template-columns: repeat(5, 1fr);
}

.info-cell {
  padding: 10px 12px;
  border-right: 1px solid #f2f3f6;
  font-size: 11px;
  min-width: 0;
}

.info-cell:last-child {
  border-right: none;
}

.cell-ref-by-spanning {
  grid-column: span 2;
  border-right: none;
}

.ck {
  color: #888;
  font-weight: 500;
  margin-bottom: 3px;
  font-size: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.cv {
  color: #1a1f36;
  font-weight: 600;
  font-size: 12px;
  word-break: break-word;
}

.wd-row-item {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  padding: 2px 0;
  border-bottom: 1px solid #f5f5f8;

  &:last-child {
    border-bottom: none;
  }
}

.btn-apply,
.btn-assign {
  padding: 3px 8px;
  font-size: 10px;
  white-space: nowrap;
}

.cell-select,
.profile-select {
  font-size: 11px;
  height: 30px !important;
}

.ref-bdm-select {
  min-width: 120px;
  font-size: 11px;

  ::v-deep .vs__dropdown-toggle {
    height: 30px;
  }
}

.deposit-limit {
  text-decoration: underline;
  cursor: pointer;
  font-weight: 700;

  &-disable {
    text-decoration: none;
    cursor: default;
    color: grey;
  }
}

.ci-revenue {
  border-top: 2px solid #e2e6ea;
}

.ci-revenue-header {
  padding: 6px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e2e6ea;
}

@media (max-width: 1400px) {
  .info-row-main {
    grid-template-columns: repeat(3, 1fr);
  }

  .cell-ref-by-spanning {
    grid-column: span 3;
    border-right: none;
  }
}

@media (max-width: 992px) {
  .info-row-main {
    grid-template-columns: repeat(2, 1fr);
  }

  .cell-ref-by-spanning {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .info-row-main {
    grid-template-columns: 1fr;
  }

  .cell-ref-by-spanning {
    grid-column: span 1;
  }

  .info-cell {
    border-right: none;
    border-bottom: 1px solid #f2f3f6;
  }

  .cell-ref-by-spanning ::v-deep .d-flex.flex-nowrap {
    flex-wrap: wrap !important;
  }

  .ref-bdm-select {
    min-width: 0;
    width: 100%;
  }

  .cell-select,
  .profile-select {
    max-width: 100%;
  }
}
</style>

<style lang="scss">
.custom-control-label {
  font-size: 11px !important;
}
</style>